import { useCallback } from 'react';
import type {
  MatterNumberingConfig,
  ConfigValidationResult,
  MatterNumberingTemplate
} from '../../../types/matterNumbering';
import { getSystemPreferences, updateSystemPreferences } from '../services/SystemPreferencesAPI';

const createDefaultEngagementNumberingConfig = (): MatterNumberingConfig => ({
  realm_id: 'system-preferences',
  isEnabled: true,
  defaultTemplate: {
    id: 'professional-standard',
    name: 'Professional Standard',
    description: 'Date-client-service area format for general engagement tracking',
    pattern: '{{YYYY}}-{{MM}}-{{DD}}-{{clientCode}}-{{practiceArea}}-{{sequence:3}}',
    category: 'professional',
    complexity: 3,
    isRecommended: true,
    examples: ['2026-05-18-ACME-OPS-001', '2026-05-18-NOVA-CS-002'],
    practiceAreas: 'all',
    status: 'enabled'
  },
  practiceAreaConfigs: [],
  customTemplates: [],
  globalSettings: {
    sequencePadding: 3,
    sequenceStart: 1,
    resetSequence: 'yearly',
    separator: '-',
    caseSensitive: false,
    allowDuplicates: false,
    validationRules: [
      {
        id: 'unique-engagement-number',
        type: 'uniqueness',
        rule: 'engagement_number_unique',
        errorMessage: 'Engagement number must be unique for this subscriber',
        isEnabled: true
      }
    ]
  },
  lastUpdatedBy: 'system',
  lastUpdatedDate: new Date().toISOString()
});

const getStoredEngagementNumberingConfig = (preferences: Awaited<ReturnType<typeof getSystemPreferences>>): MatterNumberingConfig => {
  const storedConfig = (preferences.ui_app_defaults as any)?.engagement_numbering_config;
  return storedConfig && typeof storedConfig === 'object'
    ? { ...createDefaultEngagementNumberingConfig(), ...storedConfig }
    : createDefaultEngagementNumberingConfig();
};

export const useMatterNumbering = () => {
  const getConfig = useCallback(async (): Promise<MatterNumberingConfig | null> => {
    try {
      console.log('🔄 [MatterNumbering] Loading engagement configuration from system preferences...');

      const preferences = await getSystemPreferences();
      const config = getStoredEngagementNumberingConfig(preferences);
      console.log('✅ [MatterNumbering] Loaded config with', config.customTemplates?.length || 0, 'custom templates');
      return config;
    } catch (error) {
      console.error('❌ [MatterNumbering] Error loading configuration:', error);
      throw error;
    }
  }, []);

  const saveConfig = useCallback(async (config: MatterNumberingConfig): Promise<MatterNumberingConfig> => {
    try {
      console.log('🔄 [MatterNumbering] Saving configuration to backend...', config);

      const preferences = await getSystemPreferences();
      const configToSave = {
        ...config,
        lastUpdatedDate: new Date().toISOString()
      };
      await updateSystemPreferences({
        ui_app_defaults: {
          ...((preferences.ui_app_defaults || {}) as any),
          engagement_numbering_config: configToSave,
        } as any,
      });

      console.log('✅ [MatterNumbering] Configuration saved successfully');
      return configToSave;
    } catch (error) {
      console.error('❌ [MatterNumbering] Error saving configuration:', error);
      throw error;
    }
  }, []);

  const validateConfig = useCallback(async (config: MatterNumberingConfig): Promise<ConfigValidationResult> => {
    try {
      console.log('🔄 [MatterNumbering] Validating configuration...');

      const errors = !config.defaultTemplate?.pattern
        ? [{ field: 'pattern', message: 'A default engagement numbering pattern is required', severity: 'error' as const }]
        : [];
      console.log('✅ [MatterNumbering] Validation completed');
      return {
        isValid: errors.length === 0,
        errors,
        warnings: [],
        suggestions: []
      };
    } catch (error) {
      console.error('❌ [MatterNumbering] Error validating configuration:', error);
      throw error;
    }
  }, []);

  const getTemplates = useCallback(async (): Promise<MatterNumberingTemplate[]> => {
    try {
      const config = await getConfig();
      return [
        config?.defaultTemplate || createDefaultEngagementNumberingConfig().defaultTemplate,
        ...(config?.customTemplates || [])
      ];
    } catch (error) {
      console.error('❌ [MatterNumbering] Error loading templates:', error);
      throw error;
    }
  }, [getConfig]);

  const getPracticeAreas = useCallback(async (): Promise<string[]> => {
    try {
      const preferences = await getSystemPreferences();
      return (preferences.service_areas || [])
        .filter((area) => area.enabled !== false)
        .map((area) => area.label || area.code)
        .filter(Boolean);
    } catch (error) {
      console.error('❌ [MatterNumbering] Error loading service areas:', error);
      throw error;
    }
  }, []);

  const resetConfig = useCallback(async (): Promise<MatterNumberingConfig> => {
    try {
      const defaultConfig = createDefaultEngagementNumberingConfig();
      await saveConfig(defaultConfig);
      return defaultConfig;
    } catch (error) {
      console.error('❌ [MatterNumbering] Error resetting configuration:', error);
      throw error;
    }
  }, [saveConfig]);

  return {
    getConfig,
    saveConfig,
    validateConfig,
    getTemplates,
    getPracticeAreas,
    resetConfig
  };
};
